/**
 * AdminHustleRoundupPage — agency-owner console.
 * Reads/writes /api/hustleroundup/config + shows cost/telemetry panel.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Users, Radio, HardDrive, DollarSign } from "lucide-react";
import { apiFetch, fetchMe } from "@/lib/api";

const inputCls = "w-full px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm";

export default function AdminHustleRoundupPage() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [tele, setTele] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const loadAll = async () => {
    try {
      const [c, t] = await Promise.all([
        apiFetch("/hustleroundup/config"),
        apiFetch("/hustleroundup/telemetry").catch(() => null),
      ]);
      setCfg(c);
      setTele(t);
    } catch (e) { setErr(e.message || "Failed to load config"); }
  };

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) { nav("/login?next=/admin/hustleroundup"); return; }
      if (!["host", "owner", "admin"].includes(u.role)) {
        setErr("Agency owner / host role required");
        return;
      }
      await loadAll();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        token_ttl_minutes: Number(cfg.token_ttl_minutes),
        max_participants:  Number(cfg.max_participants),
        room_prefix:       String(cfg.room_prefix || "hr_"),
        recording_enabled: !!cfg.recording_enabled,
      };
      await apiFetch("/hustleroundup/config", { method: "PUT", body: JSON.stringify(payload) });
      toast.success("Config saved");
      await loadAll();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-stone-950 text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-xl font-bold mb-2" data-testid="admin-error">{err}</div>
          <button onClick={() => nav("/")} className="mt-4 px-4 py-2 rounded-full bg-emerald-500 text-stone-950 font-semibold text-sm">
            Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-hustleroundup" className="min-h-screen bg-stone-950 text-white px-6 py-10">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => nav("/")} data-testid="admin-back"
                className="text-stone-400 hover:text-white text-sm flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-3xl font-bold mb-2">HustleRoundup Admin</h1>
        <p className="text-stone-400 text-sm mb-8">
          Agency-wide defaults for HustleRoundup. Applies to every new room.
        </p>

        {/* Telemetry cards */}
        {tele && (
          <div data-testid="admin-telemetry" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
            <TeleCard icon={Radio}     label="Meetings"          value={tele.meetings.total}                     hint={`${tele.meetings.active} live`} testid="tele-meetings" />
            <TeleCard icon={Users}     label="Participant-min"   value={tele.participant_minutes.toFixed(1)}     hint={`${tele.meeting_minutes.toFixed(1)} meeting-min`} testid="tele-partmin" />
            <TeleCard icon={HardDrive} label="Recordings"        value={tele.recordings.count}                   hint={`${tele.recordings.gb.toFixed(2)} GB`} testid="tele-recordings" />
            <TeleCard icon={DollarSign} label="Cost est. (USD)"  value={`$${tele.cost_estimate_usd.total.toFixed(2)}`} hint={`LK $${tele.cost_estimate_usd.livekit_participants.toFixed(2)} · Egress $${tele.cost_estimate_usd.egress.toFixed(2)}`} testid="tele-cost" />
          </div>
        )}

        {!cfg ? (
          <div className="text-stone-500 text-sm animate-pulse">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-stone-900/40 border border-stone-900 rounded-2xl p-6">
            <Field label="Token TTL (minutes)">
              <input type="number" min={1} max={120}
                     data-testid="admin-ttl"
                     value={cfg.token_ttl_minutes}
                     onChange={(e) => setCfg({ ...cfg, token_ttl_minutes: e.target.value })}
                     className={inputCls} />
            </Field>
            <Field label="Max participants">
              <input type="number" min={2} max={500}
                     data-testid="admin-max"
                     value={cfg.max_participants}
                     onChange={(e) => setCfg({ ...cfg, max_participants: e.target.value })}
                     className={inputCls} />
            </Field>
            <Field label="Room prefix">
              <input type="text"
                     data-testid="admin-prefix"
                     value={cfg.room_prefix || ""}
                     onChange={(e) => setCfg({ ...cfg, room_prefix: e.target.value })}
                     className={inputCls} />
            </Field>
            <Field label="Cloud recording enabled">
              <label className="flex items-center gap-3 pt-2.5 text-sm cursor-pointer">
                <input type="checkbox"
                       data-testid="admin-recording-toggle"
                       checked={!!cfg.recording_enabled}
                       onChange={(e) => setCfg({ ...cfg, recording_enabled: e.target.checked })}
                       className="w-4 h-4 accent-emerald-500" />
                Allow LiveKit Egress recordings
              </label>
            </Field>
            <div className="md:col-span-2">
              <button
                onClick={save}
                data-testid="admin-save"
                disabled={busy}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function TeleCard({ icon: Icon, label, value, hint, testid }) {
  return (
    <div data-testid={testid} className="p-4 rounded-xl border border-stone-900 bg-stone-900/40">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500 mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-bold tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-stone-500 mt-0.5">{hint}</div>}
    </div>
  );
}
